import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import InnerHTML from '@Components/InnerHTML';
import { COMPOUND_ID_PREFIXES, SUBSTANCE_DETAILS_TOOLTIPS } from '@Constants/ElementConstants';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import { Card } from '@els/els-react--card';
import { ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import Slugify from '@Utils/Slugify';

import './SubstanceIdentificationInformation.scss';

const IDENTIFICATION_INFORMATION_CLASS = 'rx-substance-identification';
const IDENTIFICATION_HEADING = 'Identification';
const ROW_VARIANTS = {
  PAIRED_START: 'paired-start',
  PAIRED_END: 'paired-end',
  SINGLE_COLUMN: 'single-column',
};

function getCompoundId(id, db) {
  const prefix = COMPOUND_ID_PREFIXES[db.databaseId];
  return prefix ? `${prefix}-${id}` : id;
}

function getJoinedValue(value) {
  if (isEmpty(value)) {
    return null;
  }

  return isArray(value) ? value.join(', ') : value;
}

function getCompoundTypes(type) {
  if (!type) {
    return null;
  }

  return Array.isArray(type) ? type.join(', ') : type;
}

function getCompoundIdLabel(label, database) {
  return database?.isPubChem ? label : `${label}:`;
}

function getDescriptionListClassName(type, rowVariant) {
  return classnames(`${IDENTIFICATION_INFORMATION_CLASS}__${type}`, {
    [`${IDENTIFICATION_INFORMATION_CLASS}__${type}--${rowVariant}`]: Boolean(rowVariant),
  });
}

function renderDescriptionListPair(label, content, rowVariant = ROW_VARIANTS.SINGLE_COLUMN) {
  if (!content) {
    return null;
  }

  return (
    <dl className={`${IDENTIFICATION_INFORMATION_CLASS}__pair`} key={label}>
      <dt className={getDescriptionListClassName('term', rowVariant)}>{label}</dt>
      <dd className={getDescriptionListClassName('description', rowVariant)}>{content}</dd>
    </dl>
  );
}

function getEntryPair(firstEntry, secondEntry) {
  const hasFirstEntry = Boolean(firstEntry?.content);
  const hasSecondEntry = Boolean(secondEntry?.content);

  if (!hasFirstEntry && !hasSecondEntry) {
    return null;
  }

  if (hasFirstEntry && hasSecondEntry) {
    return [
      { ...firstEntry, rowVariant: ROW_VARIANTS.PAIRED_START },
      { ...secondEntry, rowVariant: ROW_VARIANTS.PAIRED_END },
    ];
  }

  const entry = hasFirstEntry ? firstEntry : secondEntry;
  return [{ ...entry, rowVariant: ROW_VARIANTS.SINGLE_COLUMN }];
}

function renderDescriptionListRow(entries) {
  if (!entries?.length) {
    return null;
  }

  return (
    <div
      className={classnames(`${IDENTIFICATION_INFORMATION_CLASS}__row`, {
        [`${IDENTIFICATION_INFORMATION_CLASS}__row--paired`]: entries.length > 1,
      })}
      key={entries.map(({ label }) => label).join('-')}>
      {entries.map(({ label, content, rowVariant }) => renderDescriptionListPair(label, content, rowVariant))}
    </div>
  );
}

function SubstanceIdentificationInformation({ substance, database, onToggle }) {
  const { details, sourceUrl } = substance;
  const mapped = SUBSTANCE_DETAILS_TOOLTIPS[database.id];
  const label = getValueByNs(mapped, 'id') || `${database.name} ID`;
  const compoundId = details.id ? getCompoundId(details.id, database) : null;
  const casRegistryNumbers = getJoinedValue(details.casRegistryNumbers);
  const compoundTypes = getCompoundTypes(details.type);
  const panelIdSuffix = Slugify(substance?.index ?? details.id ?? database.id ?? database.name ?? IDENTIFICATION_HEADING);
  const headingId = `substance-identification-information-heading-${panelIdSuffix}`;
  let compoundContent = null;

  if (compoundId) {
    compoundContent = sourceUrl ? (
      <LinkElement data-e2e="substance-identification-information-link" href={sourceUrl} isExternal>
        {compoundId}
      </LinkElement>
    ) : (
      <InnerHTML tag="span" html={compoundId} />
    );
  }

  const rows = [
    getEntryPair(
      {
        label: getCompoundIdLabel(label, database),
        content: compoundContent,
      },
      {
        label: 'CAS Registry Number(s):',
        content: casRegistryNumbers ? <InnerHTML tag="span" html={casRegistryNumbers} /> : null,
      }
    ),
    getEntryPair(
      {
        label: 'Molecular Formula:',
        content: details.formula ? <InnerHTML tag="span" html={details.formula} mode="escape" /> : null,
      },
      {
        label: 'Linear Structure Formula:',
        content: details.linearFormula ? <InnerHTML tag="span" html={details.linearFormula} mode="escape" /> : null,
      }
    ),
    getEntryPair(
      {
        label: 'Molecular Weight:',
        content: details.weight ? <InnerHTML tag="span" html={details.weight} /> : null,
      },
      {
        label: 'InChIKey:',
        content: details.inChiKey ? <InnerHTML tag="span" html={details.inChiKey} /> : null,
      }
    ),
    getEntryPair({
      label: 'Compound Type(s):',
      content: compoundTypes ? <InnerHTML tag="span" html={compoundTypes} /> : null,
    }),
  ].filter(Boolean);

  return (
    <div id={`substance-identification-information-${panelIdSuffix}`} className="substance-results-information u-els-font-size-base">
      <Card
        className="substance-fact-details-card substance-identification-information-card"
        heading={
          <div className="substance-fact-details-card__header">
            <h3 id={headingId} className="substance-fact-details-card__title">
              {IDENTIFICATION_HEADING}
            </h3>
            <ButtonWithIcon
              data-e2e="substance-identification-information-close"
              type="borderless"
              size="small"
              sprite="Close"
              iconRight
              iconOnly
              className="substance-fact-details-card__close"
              aria-label={`Close ${IDENTIFICATION_HEADING}`}
              onClick={onToggle}>
              Close {IDENTIFICATION_HEADING}
            </ButtonWithIcon>
          </div>
        }
        headingLevel="h3"
        // RX-23573: no `role="region"` — per-result panel landmark overuse (ARIA APG). `<h3>` heading retained.
        aria-labelledby={headingId}>
        <div
          className={`${IDENTIFICATION_INFORMATION_CLASS} ${IDENTIFICATION_INFORMATION_CLASS}--description-list ${IDENTIFICATION_INFORMATION_CLASS}--paired-layout results-key-value-wrapper`}>
          {rows.map(renderDescriptionListRow)}
        </div>
      </Card>
    </div>
  );
}

SubstanceIdentificationInformation.propTypes = {
  database: PropTypes.object.isRequired,
  substance: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default SubstanceIdentificationInformation;
